import * as React from "react";
import { useReducer, useEffect } from "react";
import { Row, Col, Button, Input } from 'reactstrap';
import GenericDropdown from './controls/GenericDropdown';
import { LookupItem } from "../Models/LookupItem";
import NVKBListingResults, { SearchFilter, NVKBItem } from './NVKBListingResult';
import * as Utils from '../Utils';

type SortItemValue = {
    field: 'date' | 'alpha',
    sortAsc: boolean
}

type SortItem = {
    Value: SortItemValue,
    Title: string
}

const sortItems: SortItem[] = [{
    Title: 'Alpha',
    Value: {
        field: 'alpha',
        sortAsc: false
    }
}, {
    Title: 'Date ASC',
    Value: {
        field: 'date',
        sortAsc: true
    }
}, {
    Title: 'Date DESC',
    Value: {
        field: 'date',
        sortAsc: false
    }
}];

const defaultSortItem = sortItems[2];


type NVKBListingState = {
    keywords: string;
    selectedCategory: LookupItem;
    selectedMedia: LookupItem;
    selectedBranch: LookupItem;
    searchFilter: SearchFilter;
    orderBy: SortItemValue;
    correctBranches: LookupItem[],
    correctBranchLabel: string;
    initDone: boolean;
}

const initialState: NVKBListingState = {
    keywords: '',
    selectedCategory: undefined,
    selectedMedia: undefined,
    selectedBranch: undefined,
    searchFilter: undefined,
    orderBy: defaultSortItem.Value,
    correctBranches: undefined,
    correctBranchLabel: 'No Sub-Cats Available',
    initDone: false
}


const NVKBListing = (props: {
    Path: string,
    Categories: LookupItem[],
    Branches: LookupItem[],
    ItemsCategoryStructure: ItemCategoryStructure[],
    Summary: string
}) => {

    let [state, setState] = useReducer(
        (prevState: NVKBListingState, newState: Partial<NVKBListingState>) => ({ ...prevState, ...newState }),
        initialState
    );

    const search = () => {
        let sf: SearchFilter = {
            Category: state.selectedCategory ? state.selectedCategory.Value : undefined,
            Branch: state.selectedBranch ? state.selectedBranch.Value : undefined,
            Keywords: state.keywords,
            SortByField: state.orderBy.field,
            SortASC: state.orderBy.sortAsc,
            Path: props.Path
        };

        setState({ searchFilter: sf });
    };

    const cleanFilters = () => {

        let sf: SearchFilter = {
            Category: undefined,
            Branch: undefined,
            Keywords: '',
            SortByField: state.orderBy.field,
            SortASC: state.orderBy.sortAsc,
            Path: props.Path
        };

        setState({
            selectedBranch: undefined,
            selectedCategory: undefined,
            correctBranches: [],
            correctBranchLabel: 'No Sub-Cats Available',
            keywords: '',
            searchFilter: sf
        });
    }

    useEffect(() => {

        let hp: HashParams = {
            cat: state.selectedCategory ? state.selectedCategory.Title : undefined,
            branch: state.selectedBranch ? state.selectedBranch.Title : undefined,
            media: state.selectedMedia ? state.selectedMedia.Title : undefined,
            q: state.keywords,
            sort: state.orderBy.field,
            asc: state.orderBy.sortAsc
        };

        if (state.initDone) {
            window.location.href = Utils.buildHash(hp);
        }

    }, [state.searchFilter]);


    useEffect(() => {

        let newState: Partial<NVKBListingState> = {};

        if (location.hash && location.hash.length > 2) {
            let urlParams = Utils.hashStringParse(location.hash) as HashParams;

            // set category
            if (urlParams.cat) {
                newState.selectedCategory = props.Categories.find(x => x.Title.toLowerCase() == urlParams.cat.toLowerCase());
            }

            // set branch
            if (urlParams.branch) {
                newState.selectedBranch = props.Branches.find(x => x.Title.toLowerCase() == urlParams.branch.toLowerCase());
            }

            // set keyword
            if (urlParams.q && urlParams.q.length > 0) {
                newState.keywords = urlParams.q;
            }

            // order by
            if (urlParams.sort && (urlParams.sort.toLowerCase() == 'date' || urlParams.sort.toLowerCase() == 'alpha')) {

                urlParams.asc = urlParams.asc || false;
                var sortItem = sortItems.find(x => x.Value.field == urlParams.sort.toLowerCase() && x.Value.sortAsc == urlParams.asc);
                newState.orderBy = sortItem ? sortItem.Value : defaultSortItem.Value;
            }
        }

        if (!newState.orderBy) {
            newState.orderBy = defaultSortItem.Value;
        }

        newState.initDone = true;
        newState.searchFilter = {
            Category: newState.selectedCategory ? newState.selectedCategory.Value : undefined,
            Branch: newState.selectedBranch ? newState.selectedBranch.Value : undefined,
            Keywords: newState.keywords,
            SortByField: newState.orderBy.field,
            SortASC: newState.orderBy.sortAsc,
            Path: props.Path
        };

        setState(newState);

    }, []);

    const searchByCategory = (categoryId: string) => {
        console.log('search by cate', categoryId);
        let category = props.Categories.find(x => x.Value == categoryId);

        let sf: SearchFilter = {
            Category: category ? category.Value : undefined,
            Branch: undefined,
            Keywords: '',
            SortByField: state.orderBy.field,
            SortASC: state.orderBy.sortAsc,
            Path: props.Path
        };

        let newState: Partial<NVKBListingState> = {
            selectedCategory: category,
            searchFilter: sf,
            selectedBranch: undefined,
            keywords: ''
        }

        setState(newState);
        updateCorrectBranches(categoryId);
    }

    const searchByBranch = (branchId: string) => {

        console.log('searchByBranch', branchId);
        let branch = props.Branches.find(x => x.Value == branchId);

        let sf: SearchFilter = {
            Category: undefined,
            Branch: branch ? branch.Value : undefined,
            Keywords: '',
            SortByField: state.orderBy.field,
            SortASC: state.orderBy.sortAsc,
            Path: props.Path
        };

        let newState: Partial<NVKBListingState> = {
            selectedCategory: undefined,
            selectedBranch: branch,
            searchFilter: sf,
            keywords: ''
        }

        setState(newState);
    }

    const updateCorrectBranches = (categoryId: string) => {

        let theCorrectBranches: LookupItem[] = [];
        props.ItemsCategoryStructure.forEach((x) => {
            if (x.CategoryGUID == categoryId) {
                theCorrectBranches.push(props.Branches.find(y => y.Value == x.BranchGUID));
            }
        });

        setState({ correctBranches: theCorrectBranches });
        setState({ selectedBranch: undefined, correctBranchLabel: 'All Sub-Categories' });
    }

    // return
    return <Row>
        <Col md={12} lg={12}>
            <div className="description" dangerouslySetInnerHTML={{ __html: props.Summary }}>
            </div>
            <div className="news-listing">
                <div className="inline-filters d-flex flex-xl-row flex-column flex-lg-column">
                    <div className="d-xl-flex w-100">
                        <GenericDropdown
                            dataSource={props.Categories}
                            getId={x => x.Value}
                            renderItem={x => x.Title}
                            renderSelectedItem={x => x.Title}
                            nullSelectionText="Choose Category"
                            allowNullSelection={true}
                            selectedValue={state.selectedCategory ? state.selectedCategory.Value : undefined}
                            onSelectedItemChanged={x => (setState({ selectedCategory: x }), updateCorrectBranches(x.Value))}
                        />

                        <GenericDropdown
                            dataSource={state.correctBranches}
                            getId={x => x.Value}
                            renderItem={x => x.Title}
                            renderSelectedItem={x => x.Title}
                            selectionPreDecoration={<i className="fa fa-sliders-h mr-2"></i>}
                            //nullSelectionText={state.correctBranchLabel}
                            nullSelectionText="Sub-Categories"
                            allowNullSelection={true}
                            selectedValue={state.selectedBranch ? state.selectedBranch.Value : undefined}
                            onSelectedItemChanged={x => (setState({ selectedBranch: x }))}
                        />


                        <div className="search">
                            <div className="input-group w-100">
                                <div className="search-icon">
                                    <i className="fas fa-search"></i>
                                </div>
                                <Input onChange={(e) => { setState({ keywords: e.target.value }) }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            search();
                                        }
                                    }}
                                    value={state.keywords}
                                    className="form-control"
                                    placeholder="Search for keywords" />

                                <Button onClick={search}>Search</Button>
                                <Button onClick={cleanFilters} className="btn-clear">Clear Search</Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="clearfix"></div>
                <NVKBListingResults searchFilter={state.searchFilter}
                    searchByCategory={searchByCategory}
                    searchByBranch={searchByBranch}
                />

            </div>

        </Col>
    </Row>

}

type HashParams = {
    cat: string,
    branch: string,
    media: string,
    q: string,
    sort: string
    asc: boolean;
}

export default NVKBListing;

class ItemCategoryStructure {

    CategoryGUID: string;
    BranchGUID: string;

}